import { useState } from "react";
import { Link, useParams, useLocation } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { useCanAccessNav } from "@/lib/permissions";
import { useQuery } from "@tanstack/react-query";
import { isForbidden, useEvent, useEventPosGrants, useOrders, useTransactions } from "@/lib/api/queries";
import { apiClient } from "@/lib/api/client";
import { useAuth } from "@/lib/auth";
import { Capacitor } from "@capacitor/core";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Segment-aware active match: '/support' must NOT light up on
// '/financials/support-reloads' (raw substring matching did), but must on
// '/support' itself and any '/support/...' subpage.
const pathMatchesSegment = (pathname: string, match: string) =>
  pathname.endsWith(match) || pathname.includes(`${match}/`);

interface NavItem {
  name: string;
  href: string;
  icon: string;
  navKey: string;
  eventScoped?: boolean;
  matchPath?: string;
  showBadge?: boolean;
  badgeColor?: string;
  external?: boolean;
  beta?: boolean;
}

// Feather icons as SVG paths (matching the original exactly)
const icons: Record<string, React.ReactNode> = {
  shield: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  calendar: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),
  toolkit: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
    </svg>
  ),
  "bar-chart": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <line x1="12" y1="20" x2="12" y2="10"></line>
      <line x1="18" y1="20" x2="18" y2="4"></line>
      <line x1="6" y1="20" x2="6" y2="16"></line>
    </svg>
  ),
  headphones: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
    </svg>
  ),
  "rotate-cw": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <polyline points="23 4 23 10 17 10"></polyline>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
    </svg>
  ),
  "shopping-cart": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
    </svg>
  ),
  "refresh-cw": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <polyline points="23 4 23 10 17 10"></polyline>
      <polyline points="1 20 1 14 7 14"></polyline>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
  ),
  users: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  "map-pin": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
      <circle cx="12" cy="10" r="3"></circle>
    </svg>
  ),
  list: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <line x1="8" y1="6" x2="21" y2="6"></line>
      <line x1="8" y1="12" x2="21" y2="12"></line>
      <line x1="8" y1="18" x2="21" y2="18"></line>
      <line x1="3" y1="6" x2="3.01" y2="6"></line>
      <line x1="3" y1="12" x2="3.01" y2="12"></line>
      <line x1="3" y1="18" x2="3.01" y2="18"></line>
    </svg>
  ),
  "file-text": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  ),
  gift: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <polyline points="20 12 20 22 4 22 4 12"></polyline>
      <rect x="2" y="7" width="20" height="5"></rect>
      <line x1="12" y1="22" x2="12" y2="7"></line>
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
    </svg>
  ),
  // lucide "tag"
  tag: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"></path>
      <circle cx="7.5" cy="7.5" r=".5" fill="currentColor"></circle>
    </svg>
  ),
  zap: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
    </svg>
  ),
  "bar-chart-2": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <line x1="18" y1="20" x2="18" y2="10"></line>
      <line x1="12" y1="20" x2="12" y2="4"></line>
      <line x1="6" y1="20" x2="6" y2="14"></line>
    </svg>
  ),
  layout: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="3" y1="9" x2="21" y2="9"></line>
      <line x1="9" y1="21" x2="9" y2="9"></line>
    </svg>
  ),
  "dollar-sign": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <line x1="12" y1="1" x2="12" y2="23"></line>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
    </svg>
  ),
  settings: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
    </svg>
  ),
  map: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
      <line x1="8" y1="2" x2="8" y2="18"></line>
      <line x1="16" y1="6" x2="16" y2="22"></line>
    </svg>
  ),
  x: (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  ),
  monitor: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
      <line x1="8" y1="21" x2="16" y2="21"></line>
      <line x1="12" y1="17" x2="12" y2="21"></line>
    </svg>
  ),
  activity: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
    </svg>
  ),
  server: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
      <line x1="6" y1="6" x2="6.01" y2="6"></line>
      <line x1="6" y1="18" x2="6.01" y2="18"></line>
    </svg>
  ),
  terminal: (
    // Payment terminal: handheld body, screen, keypad dots
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
      <rect x="8" y="5" width="8" height="5" rx="1" ry="1"></rect>
      <line x1="8.5" y1="14" x2="8.51" y2="14"></line>
      <line x1="12" y1="14" x2="12.01" y2="14"></line>
      <line x1="15.5" y1="14" x2="15.51" y2="14"></line>
      <line x1="8.5" y1="18" x2="8.51" y2="18"></line>
      <line x1="12" y1="18" x2="12.01" y2="18"></line>
      <line x1="15.5" y1="18" x2="15.51" y2="18"></line>
    </svg>
  ),
  smartphone: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
      <line x1="12" y1="18" x2="12.01" y2="18"></line>
    </svg>
  ),
  "credit-card": (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
      <line x1="2" y1="10" x2="22" y2="10"></line>
    </svg>
  ),
  plus: (
    <svg
      className="w-4 h-4 mr-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
};

const navItems: NavItem[] = [
  {
    name: "Events",
    href: "/events",
    icon: "calendar",
    navKey: "events",
    matchPath: "/events",
  },
];

// Org-level attendee CRM. Rendered only for users who actually administer an
// organization (or superusers) — see the membership check where it's used;
// the backend endpoint is the real boundary.
const contactsNavItem: NavItem = {
  name: "Contacts",
  href: "/contacts",
  icon: "users",
  navKey: "contacts",
  matchPath: "/contacts",
};

// EventPay-internal, superuser-only. Hiding the group is UX only — the /ops
// layout route and each endpoint enforce the real boundary. Children are
// additionally filtered by NavGroupItem via their navKey permissions, so the
// group self-hides for anyone who somehow renders it.
const adminNavGroup: NavGroup = {
  name: "Admin",
  icon: "monitor",
  navKey: "admin",
  children: [
    {
      name: "New Event",
      href: "/ops/events/new",
      icon: "plus",
      navKey: "new_event",
      matchPath: "/ops/events/new",
    },
    {
      name: "Pulse",
      href: "/ops/pulse",
      icon: "activity",
      navKey: "pulse",
      matchPath: "/ops/pulse",
    },
    {
      name: "Revenue",
      href: "/ops/revenue",
      icon: "dollar-sign",
      navKey: "revenue",
      matchPath: "/ops/revenue",
    },
    {
      name: "Risk",
      href: "/ops/fraud",
      icon: "shield",
      navKey: "fraud_admin",
      matchPath: "/ops/fraud",
    },
    {
      name: "Terminals",
      href: "/ops/terminals",
      icon: "terminal",
      navKey: "terminals_admin",
      matchPath: "/ops/terminals",
    },
    {
      name: "System",
      href: "/ops/system",
      icon: "server",
      navKey: "system_admin",
      matchPath: "/ops/system",
    },
  ],
};

type EventScopedEntry =
  | { type: "item"; item: NavItem }
  | { type: "group"; group: NavGroup };

const mobileAppGroup: NavGroup = {
  name: "Mobile App",
  icon: "smartphone",
  navKey: "mobile_app",
  children: [
    {
      name: "Home Layout",
      href: "/home-layout",
      icon: "layout",
      navKey: "home_layout",
      eventScoped: true,
      matchPath: "/home-layout",
    },
    {
      name: "Event Content",
      href: "/event-content",
      icon: "file-text",
      navKey: "event_content",
      eventScoped: true,
      matchPath: "/event-content",
    },
  ],
};

const vendorMenuGroup: NavGroup = {
  name: "Vendor & Menu",
  icon: "list",
  navKey: "vendor_menu",
  children: [
    {
      name: "Menu Items",
      href: "/menu",
      icon: "list",
      navKey: "menu",
      eventScoped: true,
      matchPath: "/menu",
    },
    {
      name: "Locations",
      href: "/vendors",
      icon: "map-pin",
      navKey: "vendor_locations",
      eventScoped: true,
      matchPath: "/vendors",
    },
    {
      name: "Promotions",
      href: "/promotions",
      icon: "tag",
      navKey: "promotions",
      eventScoped: true,
      matchPath: "/promotions",
    },
  ],
};

const reportingStatsGroup: NavGroup = {
  name: "Reporting & Stats",
  icon: "bar-chart-2",
  navKey: "reporting_stats",
  children: [
    {
      name: "Reporting",
      href: "/reporting",
      icon: "bar-chart-2",
      navKey: "reporting",
      eventScoped: true,
      matchPath: "/reporting",
    },
    {
      name: "Site Map",
      href: "/site-map",
      icon: "map",
      navKey: "site_map",
      eventScoped: true,
      matchPath: "/site-map",
    },
    {
      name: "Live Stats",
      href: "/live-stats",
      icon: "activity",
      navKey: "live_stats",
      eventScoped: true,
      matchPath: "/live-stats",
    },
    {
      name: "Financials",
      href: "/financials",
      icon: "dollar-sign",
      navKey: "financials",
      eventScoped: true,
      matchPath: "/financials",
    },
  ],
};

const settingsGroup: NavGroup = {
  name: "Settings",
  icon: "settings",
  navKey: "settings_group",
  children: [
    {
      name: "Risk",
      href: "/fraud",
      icon: "shield",
      navKey: "fraud",
      eventScoped: true,
      matchPath: "/fraud",
    },
    {
      name: "Toolkit",
      href: "/toolkit",
      icon: "toolkit",
      navKey: "toolkit",
      eventScoped: true,
      matchPath: "/toolkit",
    },
    {
      name: "Event Settings",
      href: "/settings",
      icon: "settings",
      navKey: "event_settings",
      eventScoped: true,
      matchPath: "/settings",
    },
  ],
};

const eventScopedEntries: EventScopedEntry[] = [
  {
    type: "item",
    item: {
      name: "Dashboard",
      href: "/dashboard",
      icon: "bar-chart",
      navKey: "dashboard",
      eventScoped: true,
      matchPath: "/dashboard",
    },
  },
  {
    type: "item",
    item: {
      name: "Support",
      href: "/support",
      icon: "headphones",
      navKey: "support",
      eventScoped: true,
      matchPath: "/support",
    },
  },
  {
    type: "item",
    item: {
      name: "Reload",
      href: "/reload",
      icon: "rotate-cw",
      navKey: "reload",
      eventScoped: true,
      matchPath: "/reload",
    },
  },
  {
    type: "item",
    item: {
      name: "Orders",
      href: "/orders",
      icon: "shopping-cart",
      navKey: "orders",
      eventScoped: true,
      matchPath: "/orders",
      showBadge: true,
      badgeColor: "bg-green-600",
    },
  },
  {
    type: "item",
    item: {
      name: "Transactions",
      href: "/transactions",
      icon: "refresh-cw",
      navKey: "transactions",
      eventScoped: true,
      matchPath: "/transactions",
      showBadge: true,
      badgeColor: "bg-green-600",
    },
  },
  {
    type: "item",
    item: {
      name: "Users",
      href: "/users",
      icon: "users",
      navKey: "users",
      eventScoped: true,
      matchPath: "/users",
    },
  },
  { type: "group", group: vendorMenuGroup },
  { type: "group", group: mobileAppGroup },
  {
    type: "item",
    item: {
      name: "Vouchers",
      href: "/vouchers",
      icon: "gift",
      navKey: "vouchers",
      eventScoped: true,
      matchPath: "/vouchers",
    },
  },
  {
    type: "item",
    item: {
      name: "Integrations",
      href: "/integrations",
      icon: "zap",
      navKey: "integrations",
      eventScoped: true,
      matchPath: "/integrations",
    },
  },
  {
    type: "item",
    item: {
      name: "Card payments",
      href: "/card-payments",
      icon: "credit-card",
      navKey: "card_phones",
      eventScoped: true,
      matchPath: "/card-payments",
    },
  },
  { type: "group", group: reportingStatsGroup },
  { type: "group", group: settingsGroup },
  {
    type: "item",
    item: {
      name: "Point of Sale",
      href: "https://pos.eventpay.com",
      icon: "monitor",
      navKey: "pos",
      external: true,
    },
  },
];

interface NavGroup {
  name: string
  icon: string
  navKey: string
  children: NavItem[]
}

// Format number with k suffix for thousands
function formatBadgeCount(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(2)}k`;
  }
  return count.toLocaleString();
}

function NavLink({
  item,
  eventId,
  onClose,
  badge,
}: {
  item: NavItem;
  eventId?: string;
  onClose: () => void;
  badge?: number;
}) {
  const location = useLocation();
  const canAccess = useCanAccessNav(item.navKey);

  if (!canAccess) return null;

  const href =
    item.eventScoped && eventId ? `/events/${eventId}${item.href}` : item.href;

  const isActive =
    !item.external &&
    (item.eventScoped
      ? pathMatchesSegment(location.pathname, item.matchPath || item.href)
      : location.pathname === item.href ||
        location.pathname === `${item.href}/`);

  const linkClasses = cn(
    "flex items-center px-3 py-2 rounded-md transition-colors text-sm font-medium",
    isActive
      ? "bg-blue-500 text-white font-semibold"
      : "text-neutral-800 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-700",
  );

  // External links use <a> tag with target="_blank"
  if (item.external) {
    return (
      <li>
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className={linkClasses}
        >
          {icons[item.icon]}
          <span className="flex-1">{item.name}</span>
          <svg
            className="w-3 h-3 ml-1 opacity-50"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link to={href} preload={false} onClick={onClose} className={linkClasses}>
        {icons[item.icon]}
        <span className="flex-1">{item.name}</span>
        {item.beta && (
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold leading-none rounded bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
            Beta
          </span>
        )}
        {item.showBadge && badge !== undefined && badge > 0 && (
          <span
            className={cn(
              "ml-2 px-2 py-0.5 text-xs font-medium text-white rounded-full min-w-[1.5rem] text-center",
              item.badgeColor || "bg-green-600",
            )}
          >
            {formatBadgeCount(badge)}
          </span>
        )}
      </Link>
    </li>
  );
}

function NavGroupItem({
  group,
  eventId,
  onClose,
  badges,
}: {
  group: NavGroup
  eventId?: string
  onClose: () => void
  badges: Record<string, number>
}) {
  const location = useLocation()
  const childAccess = group.children.map(c => ({ item: c, canAccess: useCanAccessNav(c.navKey) }))
  const visibleChildren = childAccess.filter(c => c.canAccess)

  const isChildActive = visibleChildren.some(({ item }) =>
    item.eventScoped
      ? pathMatchesSegment(location.pathname, item.matchPath || item.href)
      : location.pathname === item.href
  )

  const [open, setOpen] = useState(isChildActive)

  if (visibleChildren.length === 0) return null

  const toggleClasses = cn(
    'flex items-center w-full px-3 py-2 rounded-md transition-colors text-sm font-medium',
    isChildActive
      ? 'text-blue-600 dark:text-blue-400'
      : 'text-neutral-800 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-700'
  )

  return (
    <li>
      <button type="button" onClick={() => setOpen(prev => !prev)} className={toggleClasses}>
        {icons[group.icon]}
        <span className="flex-1 text-left">{group.name}</span>
        <svg
          className={cn('w-4 h-4 transition-transform', open && 'rotate-180')}
          fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <ul className="mt-1 ml-5 space-y-1 border-l border-neutral-200 dark:border-neutral-700 pl-3">
          {visibleChildren.map(({ item }) => (
            <NavLink
              key={item.navKey}
              item={item}
              eventId={eventId}
              onClose={onClose}
              badge={badges[item.navKey]}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const params = useParams({ strict: false });
  const eventId = (params as { eventId?: string }).eventId;
  const eventIdNum = eventId ? Number(eventId) : 0;

  const { data: event } = useEvent(eventIdNum);
  const { user, token } = useAuth();
  // can_add_terminal is held per event; the login's permission list is
  // across events. The phone list answers 403 at an event where it is not
  // held: hide Card payments then, and keep it on any other error.
  const canManageCardPhones = useCanAccessNav("card_phones");
  const { error: posGrantsError } = useEventPosGrants(eventIdNum, {
    enabled: canManageCardPhones,
  });
  const hiddenNavKeys = isForbidden(posGrantsError) ? ["card_phones"] : [];
  // Contacts is org-level: show it only to users who actually administer an
  // organization (superusers always). UX only — the endpoint enforces.
  const { data: myOrgs } = useQuery({
    queryKey: ["my-organizations"],
    queryFn: () => apiClient.getUserOrganizations(token ?? ""),
    enabled: !!token && !user?.is_superuser,
    staleTime: 5 * 60_000,
  });
  const hasOrgAccess = !!user?.is_superuser || (myOrgs?.count ?? 0) > 0;

  // Fetch counts for badges
  const { data: ordersData } = useOrders(eventIdNum, { limit: 1 });
  // ordering matters even though only `count` is read today:
  // TransactionLog has NO default ordering, so an unordered LIMIT 1 returns
  // an arbitrary heap row -- any future reader of results[0] would silently
  // get a random transaction, not the latest. -timestamp rides
  // idx_txlog_ts_action, so the ordered fetch is an index walk.
  const { data: transactionsData } = useTransactions(eventIdNum, {
    limit: 1,
    ordering: "-timestamp",
  });

  const orderCount = ordersData?.count || 0;
  const transactionCount = transactionsData?.count || 0;

  // Create badge map
  const badges: Record<string, number> = {
    orders: orderCount,
    transactions: transactionCount,
  };

  const isNative = Capacitor.isNativePlatform();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 container:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed container:sticky container:top-16 top-0 h-screen container:h-[calc(100vh-4rem)] w-64",
          "bg-white dark:bg-[#131313] border-r border-neutral-200 dark:border-neutral-800",
          "z-50 transition-transform duration-300 ease-in-out transform",
          isOpen
            ? "translate-x-0"
            : "-translate-x-full container:translate-x-0",
        )}
      >
        {isNative && (
          <div
            className="bg-white dark:bg-[#131313]"
            style={{ height: "var(--safe-area-top)" }}
          />
        )}
        {/* Logo / Events List - h-16 = 64px, px-6 = 24px */}
        <div className="h-16 flex items-center px-6 justify-between">
          <Link
            to="/events"
            className="flex flex-col min-w-0 flex-1"
            onClick={onClose}
          >
            <span className="text-xl font-bold text-neutral-900 dark:text-white hover:text-neutral-700 dark:hover:text-neutral-200 truncate">
              EventPay
            </span>
            {event && (
              <span className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5 truncate block">
                {event.name}
              </span>
            )}
          </Link>

          {/* Mobile close button */}
          <button
            className="container:hidden p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
            onClick={onClose}
          >
            {icons.x}
          </button>
        </div>

        {/* Navigation Links - px-4, space-y-2, py-4 */}
        <nav
          className={`px-4 overflow-y-auto h-[calc(100vh-4rem)] container:h-[calc(100vh-8rem)]`}
          style={{ paddingBottom: isNative ? "calc(var(--safe-area-top) + 1rem)" : "" }}
        >
          <ul className="space-y-2 py-4">
            {/* Global nav items */}
            {/* Global context only: inside an event the nav is the event's
                workspace — Admin (Pulse/Revenue) lives on the Events level. */}
            {user?.is_superuser && !eventId && (
              <NavGroupItem
                group={adminNavGroup}
                eventId={eventId}
                onClose={onClose}
                badges={badges}
              />
            )}
            {navItems.map((item) => (
              <NavLink
                key={item.navKey}
                item={item}
                eventId={eventId}
                onClose={onClose}
                badge={badges[item.navKey]}
              />
            ))}
            {!eventId && hasOrgAccess && (
              <NavLink
                item={contactsNavItem}
                eventId={eventId}
                onClose={onClose}
                badge={badges[contactsNavItem.navKey]}
              />
            )}

            {/* Event-scoped nav items */}
            {eventId &&
              eventScopedEntries.map((entry) =>
                entry.type === "group" ? (
                  <NavGroupItem
                    key={entry.group.navKey}
                    group={entry.group}
                    eventId={eventId}
                    onClose={onClose}
                    badges={badges}
                  />
                ) : hiddenNavKeys.includes(entry.item.navKey) ? null : (
                  <NavLink
                    key={entry.item.navKey}
                    item={entry.item}
                    eventId={eventId}
                    onClose={onClose}
                    badge={badges[entry.item.navKey]}
                  />
                ),
              )}
          </ul>
        </nav>
      </div>
    </>
  );
}
